import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  LocalWidgetRouterContext,
  LocalWidgetRouterState,
} from '@/core/widgets/contexts/local-widget-router-context';
import { NewsWidget } from '@/core/widgets/model/news-widget';
import DefaultWidgetContainerContext from '@/core/widgets/contexts/default-widget-container-context';
import HasWidgetPermissions from '@/core/widgets/permissions/has-widget-permissions';
import { ErrorBoundary, errorBoundaryReloadMessage } from '@/shared/components/export';
import useWidgetNavigate from '@/core/widgets/router/use-widget-navigate';
import WidgetToolbar from '@/core/widgets/components/widget-toolbar';
import { WidgetViewModeProvider } from '@/core/widgets/contexts/widget-view-mode-context';

const ErrorBoundaryOutlet = ({ widget }: { widget: NewsWidget }) => {
  const navigate = useWidgetNavigate();
  return (
    <ErrorBoundary
      errorMessage={errorBoundaryReloadMessage(() => {
        navigate(`${widget.defaultRoute}`);
      })}
    >
      <Outlet />
    </ErrorBoundary>
  );
};

export default function ReactWidgetRouter({
  children,
  widget,
}: React.PropsWithChildren<{ widget: NewsWidget }>) {
  document.title = widget.defaultTitle || 'MNI';
  return (
    <LocalWidgetRouterContext.Provider
      value={
        {
          basePath: widget.id,
          useLocal: false,
        } as LocalWidgetRouterState
      }
    >
      <DefaultWidgetContainerContext widget={widget}>
        <WidgetViewModeProvider widgetId={widget.id}>
          <div className={'d-flex flex-column h-100'}>
            <div className={'d-flex justify-content-between align-items-center mb-2'}>
              <h4 className={'mb-0 text-truncate'}>{widget.headerPanel}</h4>
              <span className={'flex-shrink-0'}>
                <WidgetToolbar widget={widget} context={'standalone'} />
              </span>
            </div>
            <div className={'flex-grow-1 overflow-hidden position-relative'}>
              <HasWidgetPermissions widget={widget}>
                <ErrorBoundaryOutlet widget={widget} />
              </HasWidgetPermissions>
            </div>
          </div>
        </WidgetViewModeProvider>
      </DefaultWidgetContainerContext>
    </LocalWidgetRouterContext.Provider>
  );
}
